/**
 * Arandu – extras da gaveta do carrinho (Horizon)
 * - Barra de frete grátis (converte o valor mínimo para a moeda do cliente)
 * - Carrossel de recomendações com "adicionar ao carrinho" integrado aos eventos do tema
 * - Editor: mantém a gaveta aberta enquanto você edita as configurações
 * - Abertura rápida da gaveta para adições feitas por apps de bundle
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const AC_CONFIG = window.AranduCartConfig || {};

if (!window.__aranduCartExtras) {
  window.__aranduCartExtras = true;

  const shopifyCurrency = () => (window.Shopify && window.Shopify.currency) || {};
  const rate = () => parseFloat(shopifyCurrency().rate || 1) || 1;
  const formatMoney = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: shopifyCurrency().active || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  };
  const cartAddUrl = () => (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  const cartJsUrl = () => ((window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart') + '.js';

  /* ---------------- Barra de frete grátis ---------------- */
  const paintShipping = () => {
    document.querySelectorAll('[data-ac-ship]').forEach((el) => {
      const total = Number(el.dataset.total) || 0;
      const goal = Math.round((Number(el.dataset.threshold) || 0) * 100 * rate());
      if (!goal) return;

      const key = `${total}/${goal}`;
      if (el.dataset.painted === key) return;
      el.dataset.painted = key;

      const left = Math.max(goal - total, 0);
      const fill = el.querySelector('[data-ac-ship-fill]');
      const msg = el.querySelector('[data-ac-ship-msg]');
      if (fill) fill.style.width = `${Math.min((total / goal) * 100, 100)}%`;
      if (msg) {
        msg.innerHTML =
          left > 0
            ? (el.dataset.msg || '').replace('[amount]', `<strong>${formatMoney(left)}</strong>`)
            : el.dataset.done || '';
      }
      el.classList.toggle('is-complete', left === 0);
    });
  };

  let frame = 0;
  const schedulePaint = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(paintShipping);
  };

  paintShipping();
  new MutationObserver(schedulePaint).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-total', 'data-painted'],
  });

  /* ---------------- Carrossel: setas ---------------- */
  document.addEventListener('click', (event) => {
    const nav = event.target instanceof Element ? event.target.closest('[data-ac-nav]') : null;
    if (!nav) return;
    const track = nav.closest('.ac-upsell')?.querySelector('.ac-upsell__track');
    if (!track) return;
    const dir = nav.getAttribute('data-ac-nav') === 'next' ? 1 : -1;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: dir * rtl * track.clientWidth * 0.86, behavior: 'smooth' });
  });

  /* ---------------- Carrossel: adicionar ao carrinho ---------------- */
  document.addEventListener('click', async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ac-add]') : null;
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();

    const card = button.closest('.ac-upsell__card');
    const select = card?.querySelector('[data-ac-variant]');
    const errorEl = card?.querySelector('[data-ac-error]');
    const variantId = String(select ? select.value : button.dataset.variantId);
    if (errorEl) errorEl.textContent = '';
    button.setAttribute('aria-busy', 'true');

    // Seções do carrinho que o tema precisa redesenhar (gaveta e/ou página do carrinho)
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    // Mesmo fluxo do formulário de produto do Horizon: avisa o tema e resolve quando terminar
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const cart = await (await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })).json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || 'Could not add this item';
        if (errorEl) errorEl.textContent = message;
        document.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, source: 'arandu-upsell', itemCount: 0, didError: true },
        });
        button.removeAttribute('aria-busy');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'arandu-upsell',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
      // O card some sozinho quando a gaveta é redesenhada (o produto já está no carrinho)
    } catch (error) {
      console.error('[Arandu cart]', error);
      deferred.reject(error);
      if (errorEl) errorEl.textContent = 'Something went wrong. Please try again.';
      button.removeAttribute('aria-busy');
    }
  });

  /* ---------------- Editor de temas: manter a gaveta aberta enquanto edita ----------------
   * Cada mudança nas configurações do tema recarrega a pré-visualização e a gaveta fechava.
   * Só no editor (nunca na loja), lembramos se ela estava aberta e reabrimos após recarregar.
   */
  if (window.Shopify && window.Shopify.designMode) {
    const EDITOR_KEY = 'arandu-editor-cart-open';
    const DRAWER_SELECTOR = 'theme-drawer#cart-drawer';
    const isCartDrawer = (event) => event.target instanceof Element && event.target.matches(DRAWER_SELECTOR);

    document.addEventListener('theme-drawer:open', (event) => {
      if (isCartDrawer(event)) sessionStorage.setItem(EDITOR_KEY, '1');
    });
    document.addEventListener('theme-drawer:close', (event) => {
      if (isCartDrawer(event)) sessionStorage.removeItem(EDITOR_KEY);
    });

    const reopenInEditor = async () => {
      if (sessionStorage.getItem(EDITOR_KEY) !== '1') return;
      await customElements.whenDefined('theme-drawer');
      const drawer = document.querySelector(DRAWER_SELECTOR);
      if (drawer && !drawer.isOpen && typeof drawer.open === 'function') drawer.open();
    };

    reopenInEditor();
    document.addEventListener('shopify:section:load', () => setTimeout(reopenInEditor, 50));
  }

  /* ---------------- Abertura rápida para apps de bundle ----------------
   * O tema abre a gaveta na hora quando o próprio tema adiciona ao carrinho.
   * Apps (ex.: bundle) adicionam por conta própria e só avisam o tema no fim,
   * depois de calcular os descontos. Aqui detectamos essas adições, abrimos a
   * gaveta imediatamente com uma barra de carregamento e atualizamos o
   * conteúdo assim que o app termina.
   */
  if (AC_CONFIG.fastOpen) {
    const EVENT_NAME = 'shopify:cart:lines-update';
    const THEME_WINDOW_MS = 150;
    let lastThemeEvent = -Infinity;
    let inflight = 0;
    let endTimer = 0;
    let safetyTimer = 0;

    // Toda adição feita pelo tema (ou via Shopify.actions) dispara este evento antes do fetch
    document.addEventListener(EVENT_NAME, () => (lastThemeEvent = performance.now()), true);

    const isCartAdd = (url) => {
      try {
        return /\/cart\/add(\.js|\.json)?\/?$/.test(new URL(String(url), window.location.origin).pathname);
      } catch {
        return false;
      }
    };
    const getDrawer = () => document.querySelector('theme-drawer#cart-drawer');
    const autoOpenEnabled = () => Boolean(document.querySelector('cart-drawer-component[auto-open]'));

    const stopLoading = () => {
      clearTimeout(safetyTimer);
      getDrawer()?.removeAttribute('data-ac-loading');
    };

    const waitForDrawerUpdate = (timeout = 1500) =>
      new Promise((resolve) => {
        const target = document.querySelector('#cart-drawer cart-items-component') || getDrawer();
        if (!target) return resolve();
        const observer = new MutationObserver(() => {
          observer.disconnect();
          clearTimeout(timer);
          requestAnimationFrame(() => resolve());
        });
        const timer = setTimeout(() => {
          observer.disconnect();
          resolve();
        }, timeout);
        observer.observe(target, { childList: true, subtree: true });
      });

    const refreshDrawer = async () => {
      const cart = await (await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })).json();
      if (!cart.items?.length) return;
      const deferred = CartLinesUpdateEvent.createPromise();
      const updated = waitForDrawerUpdate();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: String(cart.items[0].variant_id), quantity: cart.items[0].quantity }],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'arandu-external', itemCount: 0, didError: false },
      });
      await updated;
    };

    const onExternalStart = () => {
      inflight += 1;
      clearTimeout(endTimer);
      const drawer = getDrawer();
      if (!drawer || !autoOpenEnabled()) return;
      drawer.setAttribute('data-ac-loading', '');
      clearTimeout(safetyTimer);
      safetyTimer = setTimeout(stopLoading, 8000);
      if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open();
    };

    const onExternalEnd = (startedAt) => {
      inflight = Math.max(0, inflight - 1);
      clearTimeout(endTimer);
      // Espera um instante: o app costuma fazer várias adições seguidas (produto + brinde)
      endTimer = setTimeout(async () => {
        if (inflight > 0) return;
        try {
          if (lastThemeEvent < startedAt) {
            await refreshDrawer(); // o app não avisou o tema: atualizamos nós
          } else {
            await waitForDrawerUpdate(1200); // o app avisou: só esperamos o redesenho
          }
        } catch (error) {
          console.warn('[Arandu cart] refresh', error);
        }
        stopLoading();
      }, 350);
    };

    const isExternal = () => performance.now() - lastThemeEvent > THEME_WINDOW_MS;

    // fetch
    const nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' || input instanceof URL ? input : input?.url;
      const method = String(init?.method || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && url && isCartAdd(url) && isExternal()) {
        const startedAt = performance.now();
        onExternalStart();
        const request = nativeFetch.apply(this, arguments);
        request.then(
          () => onExternalEnd(startedAt),
          () => onExternalEnd(startedAt)
        );
        return request;
      }
      return nativeFetch.apply(this, arguments);
    };

    // XMLHttpRequest (apps mais antigos / jQuery)
    const nativeOpen = XMLHttpRequest.prototype.open;
    const nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__acCartAdd = String(method).toUpperCase() === 'POST' && isCartAdd(url);
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__acCartAdd && isExternal()) {
        const startedAt = performance.now();
        onExternalStart();
        this.addEventListener('loadend', () => onExternalEnd(startedAt), { once: true });
      }
      return nativeSend.apply(this, arguments);
    };
  }
}